import { ReactNode, useState } from 'react';
import Typography from '@mui/material/Typography';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { getAllDriveLabelsForManagerQuery } from '../../../api/users-api';
import { LabelActionResult } from '../../../api/drive-labels-api';
import { handleErrors } from '../../../utils/errors';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';

type Label = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

export interface LabelActionDialogProps {
  open: boolean;
  handleClose: () => void;
  labels: Label[];
  changeLogRoles: string[];
  changeLogRole: string;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmColor?: 'primary' | 'warning' | 'error';
  run: (
    labelIds: string[],
    changeLogMessage: string
  ) => Promise<LabelActionResult[]>;
  successMessage: string;
  partialMessage: (done: number, total: number, firstError: string) => string;
  errorMessage: string;
  /** Called once the request finishes, so the grid can clear its selection. */
  onDone: () => void;
}

/**
 * The flow Delete, Disable and Enable share: confirm, ask for a change log if the role needs one,
 * send one bulk request, then report success, a partial failure or an error and refresh the grid.
 */
export default function LabelActionDialog({
  open,
  handleClose,
  labels,
  changeLogRoles,
  changeLogRole,
  title,
  body,
  confirmLabel,
  confirmColor,
  run,
  successMessage,
  partialMessage,
  errorMessage,
  onDone,
}: LabelActionDialogProps) {
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      run(
        labels.map((label) => label.id ?? ''),
        changeLogMessage
      ),
    onSuccess: (results) => {
      const failures = results.filter((result) => !result.success);
      if (failures.length === 0) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: successMessage,
        });
      } else {
        setSnackbar({
          open: true,
          severity: 'warning',
          message: partialMessage(
            results.length - failures.length,
            results.length,
            failures[0].error
          ),
        });
      }
      // Exact: the per-label lock and permission queries share this key as a prefix, and a prefix
      // match would refetch one request per row.
      queryClient.invalidateQueries({
        queryKey: getAllDriveLabelsForManagerQuery().queryKey,
        exact: true,
      });
      setChangeLogMessage('');
      onDone();
      handleClose();
    },
    onError: (error) => {
      handleErrors({ error, snackbarMessage: errorMessage });
    },
    onSettled: () => setOpenChangeLogDialog(false),
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, changeLogRole)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={title}
      dialogContent={
        <>
          <Typography>{body}</Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: confirmLabel,
        color: confirmColor,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
